import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import { errorMessage } from '@/services/api/http';
import type { TodayAttendanceVM } from '@/services/api/models';
import { attendanceService, capabilitiesService } from '@/services/erpat';
import { captureAttendance } from '@/services/offline/offline-attendance';
import { captureRefusalCopy } from '@/i18n/offline-attendance.en';
import type {
  AttendanceProjection,
  CaptureInput,
  CaptureOutcome,
  OfflineContextRecord,
  OfflineReadiness,
} from '@/types/offline-attendance';
import { localDateKey } from '@/utils/format';

/** What the orchestrator publishes into the store (each field optional: a patch). */
export interface AttendanceOrchestratorState {
  readiness?: OfflineReadiness;
  projection?: AttendanceProjection | null;
  /** The scope's stored context; the store keeps a copy WITHOUT the grant token. */
  context?: OfflineContextRecord | null;
}

/** Before the orchestrator has said anything: nothing is provisioned yet. */
const INITIAL_READINESS: OfflineReadiness = { state: 'preparing', reason: 'not_provisioned', warnings: [] };

/**
 * The shared clock state (`specs/offline-attendance-v1/implementation-plan.md`,
 * task FE-9; decisions D7, D9) — the ONE state both clock surfaces (the
 * Timesheet and the Quick Clock widget) read, and the ONE `capture()` both call,
 * so a punch from either moves the other at once.
 *
 *  - `readiness`, `projection`, `context` are published by the orchestrator
 *    (`services/offline/offline-attendance.ts`) through
 *    {@link applyOrchestratorState}; views never write them. `context` is a copy
 *    with the grant TOKEN blanked: the token stays in the orchestrator and the
 *    sealed repository, never in reactive, devtools-visible state.
 *  - `confirmedToday` / `todayRows` are the display reads of today's attendance
 *    (`/me/attendance/today` + today's list), refreshed by
 *    {@link refreshDisplay}. They are DISPLAY only: the confirmed capture base
 *    comes from the orchestrator's NetworkOnly routes (D7).
 *  - `canClockInOutOnline` is the `syntry_clockinout_app` permit as ERPat last
 *    reported it (`null` = not read yet).
 *
 * `capture()` never throws; a refusal carries user-facing copy.
 */
export const useAttendanceStore = defineStore('attendance', () => {
  const readiness = ref<OfflineReadiness>(INITIAL_READINESS);
  const projection = shallowRef<AttendanceProjection | null>(null);
  const context = shallowRef<OfflineContextRecord | null>(null);
  const confirmedToday = shallowRef<TodayAttendanceVM | null>(null);
  const todayRows = shallowRef<unknown[]>([]);
  const canClockInOutOnline = ref<boolean | null>(null);
  const capturing = ref(false);
  const lastCapture = shallowRef<CaptureOutcome | null>(null);
  const error = ref<string | null>(null);

  /** Bumped by every display read and by `reset()`: only the newest read may land. */
  let displaySeq = 0;
  /** Same for the capabilities read. */
  let capabilitiesSeq = 0;
  /** Bumped by `reset()`: a capture still running for the previous session leaves no trace in this one. */
  let captureSeq = 0;

  /** Captures go to the device queue first (D9: readiness ready or degraded). */
  const queueFirst = computed(
    () => readiness.value.state === 'ready' || readiness.value.state === 'degraded',
  );

  /**
   * Whether the clock controls are offered: the online permit is confirmed, OR
   * offline capture is ready with a grant — ERPat issues a grant only to an
   * employee holding the permit, so the controls work when the app STARTS
   * offline and the permit cannot be read.
   */
  const canClockInOut = computed(() => {
    if (canClockInOutOnline.value === true) return true;
    const grant = context.value?.grant ?? null;
    return queueFirst.value && grant !== null && context.value?.capability.offlineAttendance === true;
  });

  /**
   * Save one clock action — queued on the device first when offline capture is
   * ready, else through the online POST (only with an EMPTY queue), else
   * refused with the reason's copy. A second call while one runs is `busy`;
   * so is a queue-first capture right after an online punch while ERPat has
   * not yet confirmed the base that punch moved (it is asked, bounded, before
   * the answer — plan amendment C20).
   * After an online punch the display reads are refreshed in the background: a
   * failed follow-up read never turns the committed POST into an error.
   * A capture that outlives its session (a sign-out while it ran) still answers
   * its caller, but touches nothing of the next session's state.
   */
  async function capture(input: CaptureInput): Promise<CaptureOutcome> {
    if (capturing.value) {
      return { kind: 'refused', reason: 'busy', message: captureRefusalCopy('busy') };
    }
    const seq = captureSeq;
    capturing.value = true;
    try {
      const outcome = await captureAttendance(input);
      if (seq === captureSeq) {
        lastCapture.value = outcome;
        if (outcome.kind === 'online') void refreshDisplay();
      }
      return outcome;
    } catch {
      // captureAttendance never throws; a bug must still never read as saved.
      const outcome: CaptureOutcome = { kind: 'refused', reason: 'storage', message: captureRefusalCopy('storage') };
      if (seq === captureSeq) lastCapture.value = outcome;
      return outcome;
    } finally {
      // A reset() since cleared it — and a capture of the next session may be running now.
      if (seq === captureSeq) capturing.value = false;
    }
  }

  /**
   * Re-read today's attendance for display (`today` + today's own rows).
   * Sequenced: an answer is applied only if no newer read started since, so a
   * slow, stale answer never overwrites a fresher one. A failed read keeps the
   * last known values and records why in `error`. Never rejects.
   */
  async function refreshDisplay(): Promise<void> {
    const seq = ++displaySeq;
    const key = localDateKey(new Date());
    const [todayRead, rowsRead] = await Promise.allSettled([
      attendanceService.today(),
      attendanceService.list({ start_date: key, end_date: key, per_page: 50 }).then((page) => page.items),
    ]);
    if (seq !== displaySeq) return; // a newer read (or a reset) superseded this one
    let failure: unknown = null;
    if (todayRead.status === 'fulfilled') confirmedToday.value = todayRead.value;
    else failure = todayRead.reason;
    if (rowsRead.status === 'fulfilled') todayRows.value = rowsRead.value;
    else failure ??= rowsRead.reason;
    error.value = failure === null ? null : errorMessage(failure);
  }

  /** Re-read the `syntry_clockinout_app` permit. A failed read keeps the last known answer. Never rejects. */
  async function refreshCapabilities(): Promise<void> {
    const seq = ++capabilitiesSeq;
    try {
      const caps = await capabilitiesService.summary();
      if (seq === capabilitiesSeq) canClockInOutOnline.value = caps.canClockInOut;
    } catch {
      // Offline or refused: keep what was last known (null until a read succeeds).
    }
  }

  /** The orchestrator's publication: readiness, projection, context (token blanked here). */
  function applyOrchestratorState(patch: AttendanceOrchestratorState): void {
    if (patch.readiness !== undefined) readiness.value = patch.readiness;
    if (patch.projection !== undefined) projection.value = patch.projection;
    if (patch.context !== undefined) context.value = withoutToken(patch.context);
  }

  /** Forget the session (sign-out / company switch). In-flight display reads are ignored. */
  function reset(): void {
    displaySeq += 1;
    capabilitiesSeq += 1;
    captureSeq += 1;
    readiness.value = INITIAL_READINESS;
    projection.value = null;
    context.value = null;
    confirmedToday.value = null;
    todayRows.value = [];
    canClockInOutOnline.value = null;
    capturing.value = false;
    lastCapture.value = null;
    error.value = null;
  }

  return {
    readiness,
    projection,
    context,
    confirmedToday,
    todayRows,
    canClockInOutOnline,
    capturing,
    lastCapture,
    error,
    queueFirst,
    canClockInOut,
    capture,
    refreshDisplay,
    refreshCapabilities,
    applyOrchestratorState,
    reset,
  };
});

/** A copy of `context` whose grant token is blanked (the grant id and window stay readable). */
function withoutToken(context: OfflineContextRecord | null): OfflineContextRecord | null {
  if (context === null) return null;
  return { ...context, grant: context.grant === null ? null : { ...context.grant, token: '' } };
}
